import React from "react";
import Fallback from "./Fallback";
import { trackEvent } from "../../services/gaTracking";
import { GA_EVENTS } from "../../constants/gaEvents";

/**
 * Generic React error boundary. Catches render/lifecycle errors in its
 * subtree and shows a fallback UI instead of white-screening the app.
 *
 * Props:
 * - variant: "page" (default) | "section" — controls fallback layout.
 * - fallback: optional custom node/render-fn ({ error, reset }) => node.
 * - onReset: optional callback fired when the user retries.
 * - resetKeys: array; when any value changes the boundary auto-resets
 *   (e.g. pass the current route so navigating away clears the error).
 * - label: optional string to identify where the crash happened (for logs).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError) return;
    const { resetKeys } = this.props;
    const prevKeys = prevProps.resetKeys;
    if (!Array.isArray(resetKeys) || !Array.isArray(prevKeys)) return;
    const changed =
      resetKeys.length !== prevKeys.length ||
      resetKeys.some((key, i) => !Object.is(key, prevKeys[i]));
    if (changed) {
      this.reset();
    }
  }

  componentDidCatch(error, errorInfo) {
    const { label } = this.props;
    // Never let logging itself throw and re-crash the boundary.
    try {
      console.error(
        `[ErrorBoundary${label ? `: ${label}` : ""}]`,
        error,
        errorInfo?.componentStack
      );
    } catch (_) {}

    try {
      trackEvent(GA_EVENTS.ERROR_BOUNDARY, {
        errorMessage: error?.message || String(error),
        errorBoundary: label || "root",
      });
    } catch (_) {}
  }

  reset() {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") {
      this.props.onReset();
    }
  }

  render() {
    const { hasError, error } = this.state;
    if (!hasError) {
      return this.props.children;
    }

    const { fallback, variant = "page" } = this.props;
    if (typeof fallback === "function") {
      return fallback({ error, reset: this.reset });
    }
    if (fallback) {
      return fallback;
    }
    return <Fallback variant={variant} error={error} onRetry={this.reset} />;
  }
}

export default ErrorBoundary;
